import { useState, useEffect } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  LineChart, Layers, Gauge, Loader2, Check, Shield, Scale, Zap, Target, TrendingUp, Cpu, Lock, Crown,
  Rocket, Activity, Radar, ChevronDown, ShieldCheck, TrendingDown, AlertTriangle, Power, Repeat, ShieldAlert,
} from "lucide-react";

// Rough margin-per-1.0-lot inputs (contract size x approx price). Used only for a
// client-side ESTIMATE so the user is warned before the broker rejects orders.
const CONTRACT_SIZE = { XAUUSD: 100, XAGUSD: 5000, US30: 1, NAS100: 1, GBPJPY: 100000, GBPNZD: 100000, GBPUSD: 100000 };
const APPROX_PRICE = { XAUUSD: 2345, XAGUSD: 29.85, US30: 38975, NAS100: 18240, GBPJPY: 189.5, GBPNZD: 2.105, GBPUSD: 1.265 };

const AVAILABLE = ["XAUUSD", "XAGUSD", "US30", "NAS100", "GBPJPY", "GBPNZD", "GBPUSD"];

const MODES = [
  { id: "conservative", label: "Conservative", icon: Shield, desc: "Highest-quality setups only. Fewer trades, stronger confirmation.", min: 80 },
  { id: "balanced", label: "Balanced", icon: Scale, desc: "Recommended. Solid quality with steady activity.", min: 65 },
  { id: "aggressive", label: "Aggressive", icon: Zap, desc: "More trades. Takes earlier setups with lighter confirmation.", min: 50 },
  { id: "swing", label: "Swing FX", icon: TrendingUp, desc: "Higher-timeframe swing trading (EMA 50/200, breakouts, pullbacks, S/R). Embraces volatility, dynamic SL/TP with smart trailing stop.", min: 60 },
];

const VOLATILITY = [
  { id: "Low", label: "Low", min: 78, desc: "Autotrade is more selective — fewer but higher-quality entries with stronger confirmation." },
  { id: "Medium", label: "Medium", min: 65, desc: "Normal scalping behavior — balanced activity in standard market conditions." },
  { id: "High", label: "High", min: 55, desc: "Autotrade hunts faster opportunities during strong price movement — quicker analysis & entries." },
];

const STRATEGY_RULES = [
  { k: "Endikatè", v: "RSI (5–7 peryòd) · Bollinger Bands · EMA rapid (9 & 21)" },
  { k: "Timeframe", v: "Scalping sou 1m · 3m · 5m" },
  { k: "Antre (BUY)", v: "RSI oversold (< 30) + pri touche Bollinger anba a, konfime pa EMA 9/21" },
  { k: "Antre (SELL)", v: "RSI overbought (> 70) + pri touche Bollinger anwo a, konfime pa EMA 9/21" },
  { k: "Take Profit", v: "0.5% – 1% pwofi pa trade" },
  { k: "Stop-Loss", v: "0.3% – 0.5% (obligatwa — pwoteje kapital la)" },
  { k: "Objektif", v: "Ti pwofi souvan, anpil trade pa jou (pa gwo pwofi raman)" },
];

const WORKFLOW = [
  "Autotrade Scanner", "Analyzing Market", "Entry Confirmation", "Trade Opened",
  "Managing SL/TP", "Closing Trade", "Re-scanning Market", "Re-open New Trade",
];

function ScalpingWorkflow() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % WORKFLOW.length), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mt-4 space-y-1" data-testid="scalping-workflow">
      {WORKFLOW.map((step, i) => {
        const on = i === active;
        const done = i < active;
        return (
          <div key={step}>
            <div className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-all duration-300 ${on ? "" : "border-[#14233d]"}`}
              style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)", boxShadow: "0 0 18px var(--sfx-accent-glow)" } : { borderColor: "#14233d" }}>
              <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${on ? "animate-pulse" : ""}`}
                style={{ backgroundColor: on ? "var(--sfx-accent)" : done ? "#2f7a55" : "#24406b" }} />
              <span className={`font-mono-term text-xs uppercase tracking-widest ${on ? "text-white" : "text-[#5f7ba6]"}`}>{step}</span>
              {on && <Radar className="h-3.5 w-3.5 ml-auto" style={{ color: "var(--sfx-accent)", animation: "spin 3s linear infinite" }} />}
            </div>
            {i < WORKFLOW.length - 1 && (
              <div className="flex justify-center py-0.5"><ChevronDown className="h-3 w-3 text-[#24406b]" /></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UpgradeGate({ navigate }) {
  return (
    <div className="px-5 pt-6 pb-6 fade-up" data-testid="pairs-upgrade-gate">
      <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-8 text-center">
        <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
          <Crown className="h-7 w-7" style={{ color: "var(--sfx-accent)" }} />
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight mb-2">Unlock AutoTrade</h1>
        <p className="text-sm text-[#5f7ba6] mb-6">
          Choose a subscription plan to select trading pairs and let the Smart Scalper trade for you automatically.
          Each plan unlocks specific markets and features.
        </p>
        <Button data-testid="pairs-upgrade-button" onClick={() => navigate("/app/settings")}
          className="h-11 rounded-full px-6 text-white font-semibold hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
          View Plans & Upgrade
        </Button>
      </div>
    </div>
  );
}

export default function Pairs() {
  const { bots, refreshAll, entitlements, account } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromCpi = location.state?.from === "cpi";
  const bot = bots[0];
  const allowed = entitlements?.markets || [];
  const maxTradesCap = entitlements?.max_trades || 10;
  const active = entitlements?.active;
  const tier = entitlements?.tier;

  const [selected, setSelected] = useState(bot?.pairs || []);
  const [lot, setLot] = useState(bot?.lot_size ?? 0.10);
  const [maxTrades, setMaxTrades] = useState(bot?.max_trades ?? 5);
  const [mode, setMode] = useState(bot?.mode ?? "balanced");
  const [minConfidence, setMinConfidence] = useState(() => { const n = Number(bot?.min_confidence); return !n ? 65 : n > 90 ? 65 : n; });
  const [volatility, setVolatility] = useState(bot?.volatility ?? "Medium");
  const [safetyEngine, setSafetyEngine] = useState(!!bot?.safety_engine);
  const [newsFilter, setNewsFilter] = useState(!!bot?.news_filter);
  const [timeframe, setTimeframe] = useState(bot?.timeframe ?? "");
  const [dailyLoss, setDailyLoss] = useState(bot?.daily_loss_limit ?? "");
  const [dailyTarget, setDailyTarget] = useState(bot?.daily_target_usd || "");
  const [autoClose, setAutoClose] = useState(!!bot?.auto_close_profit);
  const [takeProfit, setTakeProfit] = useState(bot?.take_profit_usd || "");
  const [safetyStop, setSafetyStop] = useState(() => Math.round((Number(bot?.safety_stop_pct) || 0.8) * 100));
  const [consecLoss, setConsecLoss] = useState(bot?.consec_loss_per_pair ?? 3);
  const [crossPause, setCrossPause] = useState(bot?.cross_pair_pause_min ?? 60);
  const [volAtr, setVolAtr] = useState(bot?.vol_circuit_atr_pct ?? 2.0);
  const [volPause, setVolPause] = useState(bot?.vol_circuit_pause_min ?? 15);
  const [closingAll, setClosingAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [btLoading, setBtLoading] = useState(false);
  const [btResult, setBtResult] = useState(null);
  const [brokerSyms, setBrokerSyms] = useState(null);
  const [brokerLinked, setBrokerLinked] = useState(false);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [showStrategies, setShowStrategies] = useState(false);

  // --- Margin safety estimate (client-side) ---------------------------------
  const leverage = Number(account?.leverage) || 100;
  const balance = Number(account?.equity ?? account?.balance) || 0;
  const lotNum = Number(lot) || 0;
  const mtNum = Number(maxTrades) || 0;
  const requiredMargin = selected.reduce((sum, p) => {
    const perLot = ((APPROX_PRICE[p] || 0) * (CONTRACT_SIZE[p] || 1)) / leverage;
    return sum + perLot * lotNum * mtNum;
  }, 0);
  const marginRatio = balance > 0 ? requiredMargin / balance : 0;
  // Warn before the broker rejects with "not enough money": >70% used = caution, >100% = danger.
  const marginLevel = !balance || !selected.length || lotNum <= 0 ? null
    : marginRatio > 1 ? "danger" : marginRatio > 0.7 ? "warn" : "ok";
  const fmtUsd = (v) => "$" + (v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  useEffect(() => {
    let on = true;
    api.get("/pairs/broker-symbols").then(({ data }) => {
      if (!on) return;
      setBrokerSyms(data.symbols || {});
      setBrokerLinked(!!data.linked);
      if (data.linked && data.symbols) {
        setSelected((s) => s.filter((p) => data.symbols[p] !== null));
      }
    }).catch(() => {});
    return () => { on = false; };
  }, [entitlements?.tier]);

  useEffect(() => {
    if (bot) {
      setSelected((bot.pairs || []).filter((p) => allowed.includes(p)));
      setLot(bot.lot_size ?? 0.10);
      setMaxTrades(Math.min(bot.max_trades ?? 5, maxTradesCap));
      setMode(bot.mode ?? "balanced");
      setMinConfidence(() => { const n = Number(bot.min_confidence); return !n ? 65 : n > 90 ? 65 : n; });
      setVolatility(bot.volatility ?? "Medium");
      setSafetyEngine(!!bot.safety_engine);
      setNewsFilter(!!bot.news_filter);
      setTimeframe(bot.timeframe ?? "");
      setDailyLoss(bot.daily_loss_limit ?? "");
      setDailyTarget(bot.daily_target_usd || "");
      setAutoClose(!!bot.auto_close_profit);
      setTakeProfit(bot.take_profit_usd || "");
      setSafetyStop(Math.round((Number(bot.safety_stop_pct) || 0.8) * 100));
      setConsecLoss(bot.consec_loss_per_pair ?? 3);
      setCrossPause(bot.cross_pair_pause_min ?? 60);
      setVolAtr(bot.vol_circuit_atr_pct ?? 2.0);
      setVolPause(bot.vol_circuit_pause_min ?? 15);
    }
  }, [bot?.id, bot?.lot_size, bot?.max_trades, bot?.mode, bot?.min_confidence, bot?.safety_engine, bot?.daily_loss_limit, bot?.news_filter, bot?.timeframe, bot?.auto_close_profit, bot?.take_profit_usd, bot?.safety_stop_pct, bot?.consec_loss_per_pair, bot?.cross_pair_pause_min, bot?.vol_circuit_atr_pct, bot?.vol_circuit_pause_min, account?.id, account?.type, entitlements?.tier]);

  if (entitlements && !active) return <UpgradeGate navigate={navigate} />;

  const toggle = (p) => {
    if (!allowed.includes(p)) return;
    setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  };

  const upgradeLabel = tier === "starter" ? "Upgrade to Pro" : "Upgrade to Elite";

  const save = async () => {
    if (!bot) { toast.error("No bot connected"); return; }
    if (selected.length === 0) { toast.error("Select at least one pair"); return; }
    const capped = Math.min(Number(maxTrades), maxTradesCap);
    setBusy(true);
    try {
      const { data } = await api.post(`/bots/${bot.id}/settings`, {
        pairs: selected, lot_size: Number(lot), max_trades: capped,
        mode, min_confidence: Number(minConfidence), volatility, safety_engine: safetyEngine,
        daily_loss_limit: dailyLoss === "" ? 0 : Number(dailyLoss),
        daily_target_usd: dailyTarget === "" ? 0 : Number(dailyTarget),
        auto_close_profit: autoClose, take_profit_usd: takeProfit === "" ? 0 : Number(takeProfit),
        news_filter: newsFilter, timeframe: timeframe || "auto",
        safety_stop_pct: Number(safetyStop) / 100,
        consec_loss_per_pair: Number(consecLoss),
        cross_pair_pause_min: Number(crossPause),
        vol_circuit_atr_pct: Number(volAtr),
        vol_circuit_pause_min: Number(volPause),
      });
      (data?.notes || []).forEach((n) => toast.info(n));
      toast.success(cameFromCpi
        ? "Konfigirasyon anrejistre — retounen nan Smart CPI Mode pou peze Start"
        : "Settings saved — the Smart Scalper is now using your configuration");
      await refreshAll();
      navigate(cameFromCpi ? "/app/cpi" : "/app");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeAll = async () => {
    if (!bot) return;
    if (!window.confirm("Fèmen TOUT trade ki louvri kounye a?")) return;
    setClosingAll(true);
    try {
      const { data } = await api.post(`/bots/${bot.id}/close-all`);
      toast.success(`${data.closed} trade fèmen${data.failed ? ` · ${data.failed} echwe` : ""}`);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClosingAll(false);
    }
  };

  const runBacktest = async () => {
    setBtLoading(true);
    setBtResult(null);
    try {
      const { data } = await api.post("/backtest/run");
      setBtResult(data);
      toast.success("Backtest fini");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBtLoading(false);
    }
  };


  return (
    <div className="px-5 pt-6 pb-6 fade-up space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Pairs & Trade Settings</h1>
        <p className="text-[#5f7ba6] text-sm mt-1">Choose the instruments Smart Scalper trades and set your risk limits.</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#14233d] bg-[#0A1120]" data-testid="scalper-version-badge">
            <Cpu className="h-3.5 w-3.5" style={{ color: "var(--sfx-accent)" }} />
            <span className="font-heading text-xs font-bold tracking-wide">Smart Scalper V2.0</span>
            <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">· M1</span>
          </div>
          {entitlements?.plan_name && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#14233d] bg-[#0A1120]" data-testid="pairs-plan-badge">
              <Crown className="h-3.5 w-3.5" style={{ color: "var(--sfx-accent)" }} />
              <span className="font-heading text-xs font-bold tracking-wide">{entitlements.plan_name}</span>
            </div>
          )}
        </div>
      </div>

      {account && (
        <div data-testid="config-account-banner"
          className="rounded-2xl px-4 py-3 flex items-center gap-3 border"
          style={{ borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" }}>
          {account.type === "propfirm"
            ? <ShieldCheck className="h-5 w-5 flex-shrink-0" style={{ color: "var(--sfx-accent)" }} />
            : <Cpu className="h-5 w-5 flex-shrink-0" style={{ color: "var(--sfx-accent)" }} />}
          <div className="min-w-0">
            <div className="font-heading text-sm font-semibold" data-testid="config-account-type">
              {account.type === "propfirm" ? "Prop Firm Account" : "MT5 Broker Account"}
            </div>
            <p className="text-[11px] text-[#8fb4e6]">
              Paramèt sa yo (Pairs, Lot Size, Max Trades, risk) sove SÈLMAN pou kalite kont sa a. Chak kalite kont gen pwòp konfigirasyon pa l.
            </p>
          </div>
        </div>
      )}

      <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <LineChart className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <span className="font-heading font-semibold">Trading Pairs</span>
          <span className="ml-auto font-mono-term text-xs text-[#5f7ba6]">{selected.length} selected</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {AVAILABLE.map((p) => {
            const unlocked = allowed.includes(p);
            const on = selected.includes(p);
            const notOnBroker = brokerLinked && brokerSyms && brokerSyms[p] === null;
            const brokerLabel = (brokerLinked && brokerSyms && brokerSyms[p]) ? brokerSyms[p] : p;
            if (!unlocked) {
              return (
                <div key={p} data-testid={`pair-${p}-locked`}
                  className="relative flex items-center justify-between px-3 py-2.5 rounded-xl border border-[#14233d] font-mono-term text-sm text-[#3f567d] cursor-not-allowed"
                  title={upgradeLabel}>
                  {p} <Lock className="h-3.5 w-3.5" />
                </div>
              );
            }
            if (notOnBroker) {
              return (
                <div key={p} data-testid={`pair-${p}-unavailable`}
                  className="relative flex flex-col items-start px-3 py-2 rounded-xl border border-[#14233d] font-mono-term text-sm text-[#3f567d] cursor-not-allowed"
                  title="Not offered by your broker">
                  <span className="line-through">{p}</span>
                  <span className="text-[9px] text-[#5f7ba6] normal-case">not on your broker</span>
                </div>
              );
            }
            return (
              <button key={p} onClick={() => toggle(p)} data-testid={`pair-${p}`}
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl border font-mono-term text-sm transition-colors ${on ? "text-white" : "border-[#14233d] text-[#7f9bc4] hover:border-[#24406b]"}`}
                style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" } : undefined}>
                {brokerLabel} {on && <Check className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />}
              </button>
            );
          })}
        </div>
        {allowed.length < AVAILABLE.length && (
          <button onClick={() => navigate("/app/settings")} data-testid="pairs-unlock-more"
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-[#24406b] text-sm text-[#8fb4e6] hover:bg-[#0f1a2e] transition-colors">
            <Lock className="h-3.5 w-3.5" /> {upgradeLabel} to unlock {AVAILABLE.length - allowed.length} more markets
          </button>
        )}
      </div>

      <div className={`border rounded-3xl p-5 bg-[#0A1120] transition-colors ${mode === "scalping_pro" ? "" : "border-[#14233d]"}`}
        style={mode === "scalping_pro" ? { borderColor: "var(--sfx-accent)" } : undefined} data-testid="scalping-pro-card">
        <button onClick={() => { setMode("scalping_pro"); setMinConfidence(VOLATILITY.find((v) => v.id === volatility)?.min ?? 65); }}
          data-testid="mode-scalping_pro" className="w-full flex items-center gap-3 text-left">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
            <Rocket className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold">Scalping Pro</span>
              <span className="font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">Forex & Prop Firm</span>
            </div>
            <p className="text-xs text-[#5f7ba6] mt-0.5">Fast professional scalping. The Smart Scalper adapts its speed and selectivity to market volatility.</p>
          </div>
          {mode === "scalping_pro" && <Check className="h-5 w-5 flex-shrink-0" style={{ color: "var(--sfx-accent)" }} />}
        </button>

        {mode === "scalping_pro" && (
          <div className="mt-5 pt-5 border-t border-[#14233d] fade-up">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
              <span className="font-heading font-semibold text-sm">Market Volatility</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {VOLATILITY.map((v) => {
                const on = volatility === v.id;
                return (
                  <button key={v.id} data-testid={`volatility-${v.id}`}
                    onClick={() => { setVolatility(v.id); setMinConfidence(v.min); }}
                    className={`px-2 py-3 rounded-2xl border font-heading text-sm font-semibold transition-colors ${on ? "text-white" : "border-[#14233d] text-[#7f9bc4] hover:border-[#24406b]"}`}
                    style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" } : undefined}>
                    {v.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[#8fb4e6] mt-3" data-testid="volatility-desc">{VOLATILITY.find((v) => v.id === volatility)?.desc}</p>

            <div className="mt-5">
              <button onClick={() => setShowWorkflow((v) => !v)} data-testid="workflow-toggle"
                className="w-full flex items-center gap-2 text-left">
                <Cpu className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
                <span className="font-heading font-semibold text-sm">Autotrade Workflow</span>
                <ChevronDown className={`ml-auto h-4 w-4 text-[#5f7ba6] transition-transform ${showWorkflow ? "rotate-180" : ""}`} />
              </button>
              {showWorkflow && (
                <>
                  <p className="text-xs text-[#5f7ba6] mt-1">Live view of how Autotrade cycles through every trade.</p>
                  <ScalpingWorkflow />
                </>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-[#14233d]" data-testid="strategy-library">
              <button onClick={() => setShowStrategies((v) => !v)} data-testid="strategy-toggle"
                className="w-full flex items-center gap-2 text-left">
                <Layers className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
                <span className="font-heading font-semibold text-sm">Smart Scalper Strategy</span>
                <span className="ml-auto font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">1 strategy · sinkronize</span>
                <ChevronDown className={`h-4 w-4 text-[#5f7ba6] transition-transform ${showStrategies ? "rotate-180" : ""}`} />
              </button>
              {showStrategies && (
                <>
                  <p className="text-xs text-[#5f7ba6] mb-3 mt-2">
                    Yon sèl strategy scalping presi, <b className="text-[#8fb4e6]">sinkronize</b> nan tout twa sistèm yo — <b className="text-[#8fb4e6]">Signal</b>, <b className="text-[#8fb4e6]">MT5 Autotrade</b> ak <b className="text-[#8fb4e6]">Prop Firm</b> — pou yo pa janm melanje. Li antre sèlman lè tout konfimasyon yo dakò, epi li mezire SL/TP dapre volatilite (ATR).
                  </p>
                  <div className="space-y-1.5">
                    {STRATEGY_RULES.map((r) => (
                      <div key={r.k} data-testid={`strategy-rule-${r.k.slice(0, 8).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                        className="flex gap-2 text-[11px] bg-[#060c18] border border-[#14233d] rounded-lg px-3 py-2">
                        <span className="font-mono-term uppercase tracking-wide text-[#5f7ba6] w-24 shrink-0">{r.k}</span>
                        <span className="text-[#c6d7f0] flex-1">{r.v}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="border rounded-3xl p-5 bg-[#0A1120] transition-colors" data-testid="min-confidence-card"
        style={{ borderColor: "var(--sfx-accent)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Gauge className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <Label className="font-heading font-semibold">Minimum Confidence</Label>
          <span className="ml-auto font-mono-term text-base font-bold" data-testid="min-confidence-value"
            style={{ color: "var(--sfx-accent)" }}>{minConfidence}/100</span>
        </div>
        <p className="text-xs text-[#5f7ba6] mb-4">
          Autotrade opens a trade the moment a pair's Opportunity Score reaches this level.
          <b className="text-[#8fb4e6]"> Lower = more trades</b>, higher = fewer but stricter. Recommended 55–70.
        </p>
        <Slider data-testid="min-confidence-slider" value={[minConfidence]} min={40} max={90} step={1}
          onValueChange={([v]) => setMinConfidence(v)} className="w-full" />
        <div className="flex justify-between mt-2 font-mono-term text-[10px] text-[#5f7ba6]">
          <span>40 · more trades</span><span>90 · strictest</span>
        </div>
        <button type="button" data-testid="min-confidence-recommended" onClick={() => setMinConfidence(60)}
          className="mt-3 text-xs px-3 py-1.5 rounded-full border border-[#24406b] text-[#8fb4e6] hover:bg-[#0f1a2e] transition-colors">
          Set recommended (60)
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
            <Label className="font-heading font-semibold">Lot Size</Label>
          </div>
          <Input type="number" step="0.01" min="0.01" value={lot} onChange={(e) => setLot(e.target.value)}
            data-testid="lot-size-input" className="bg-[#060c18] border-[#14233d] rounded-xl h-11" />
          <p className="text-xs text-[#5f7ba6] mt-2">Volume per trade (e.g. 0.10)</p>
          <p className="text-[10px] text-[#5f7ba6] mt-1" data-testid="min-lot-note">Min lot: XAUUSD/XAGUSD 0.01 · US30/NAS100 0.1</p>
        </div>
        <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Gauge className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
            <Label className="font-heading font-semibold">Max Trades <span className="text-[#5f7ba6] font-normal">(per pair)</span></Label>
          </div>
          <Input type="number" step="1" min="1" max={maxTradesCap} value={maxTrades}
            onChange={(e) => setMaxTrades(Math.min(Number(e.target.value), maxTradesCap))}
            data-testid="max-trades-input" className="bg-[#060c18] border-[#14233d] rounded-xl h-11" />
          <p className="text-xs text-[#5f7ba6] mt-2">Max open trades <b>per selected pair</b>. Plan ceiling: {maxTradesCap} total.</p>
        </div>
      </div>

      {marginLevel && marginLevel !== "ok" && (
        <div data-testid="margin-warning-card"
          className={`rounded-xl px-3 py-2 flex items-center gap-2 text-[11px] ${marginLevel === "danger" ? "bg-[#ff5b5b]/10 text-[#ff8f8f]" : "bg-[#f5c518]/10 text-[#f5c07a]"}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span data-testid="margin-warning-title">
            {marginLevel === "danger" ? "Not enough margin" : "Margin high"}: ~{fmtUsd(requiredMargin)} needed vs {fmtUsd(balance)}. Lower Lot/Max Trades or add funds.
          </span>
        </div>
      )}

      <div className="border rounded-3xl p-5 bg-[#0A1120]" style={{ borderColor: "#00F59333" }} data-testid="daily-target-card">
        <div className="flex items-center gap-2 mb-1">
          <Target className="h-4 w-4" style={{ color: "#00F593" }} />
          <Label className="font-heading font-semibold">Daily Profit Target</Label>
          <span className="ml-auto font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#00F593]">Goal</span>
        </div>
        <p className="text-xs text-[#5f7ba6] mb-3">
          Mete kantite kòb ou vle fè jodi a. Lè bot la rive nan objektif sa a, li <b className="text-[#00F593]">fèmen tout trade yo epi sispann</b> pou rès jounen an. Kite l vid pou pa gen okenn objektif.
        </p>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5f7ba6] font-mono-term text-sm">$</span>
          <Input type="number" step="1" min="0" value={dailyTarget}
            onChange={(e) => setDailyTarget(e.target.value)} placeholder="e.g. 300 (no goal if empty)"
            data-testid="daily-target-input" className="bg-[#060c18] border-[#14233d] rounded-xl h-11 pl-7" />
        </div>
      </div>

      <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5" data-testid="daily-loss-card">
        <div className="flex items-center gap-2 mb-1">
          <TrendingDown className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <Label className="font-heading font-semibold">Daily Loss Limit</Label>
          <span className="ml-auto font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">Custom</span>
        </div>
        <p className="text-xs text-[#5f7ba6] mb-3">
          The Smart Scalper stops opening new trades once today's loss reaches this amount (in your account currency). Leave empty for no limit.
        </p>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5f7ba6] font-mono-term text-sm">$</span>
          <Input type="number" step="1" min="0" value={dailyLoss}
            onChange={(e) => setDailyLoss(e.target.value)} placeholder="e.g. 200 (no limit if empty)"
            data-testid="daily-loss-input" className="bg-[#060c18] border-[#14233d] rounded-xl h-11 pl-7" />
        </div>
      </div>

      <div className="border rounded-3xl p-5 bg-[#0A1120] transition-colors"
        style={safetyEngine ? { borderColor: "var(--sfx-accent)" } : { borderColor: "#14233d" }}
        data-testid="safety-engine-card">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
            <ShieldCheck className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold">Multi-Confirmation Safety Engine</span>
              <span className="font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">Optional</span>
            </div>
            <p className="text-xs text-[#5f7ba6] mt-0.5">
              When ON, the Smart Scalper waits for full confirmation (trend, momentum, structure & candle close) before every trade — safer but fewer, slower entries. When OFF, Smart Scalper executes trades faster.
            </p>
          </div>
          <button type="button" role="switch" aria-checked={safetyEngine}
            onClick={() => setSafetyEngine((v) => !v)} data-testid="safety-engine-toggle"
            className="relative h-6 w-11 rounded-full transition-colors flex-shrink-0 mt-0.5"
            style={{ backgroundColor: safetyEngine ? "var(--sfx-accent)" : "#1c2b45" }}>
            <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
              style={{ left: safetyEngine ? "22px" : "2px" }} />
          </button>
        </div>
      </div>

      <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5" data-testid="timeframe-card">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <Label className="font-heading font-semibold">Timeframe</Label>
          <span className="ml-auto font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">Optional</span>
        </div>
        <p className="text-xs text-[#5f7ba6] mb-3">
          Choose the chart timeframe Smart Scalper analyses. <b className="text-[#8fb4e6]">Auto</b> lets the engine pick the best timeframe based on your mode &amp; volatility.
        </p>
        <div className="grid grid-cols-5 gap-2">
          {[["", "Auto"], ["M1", "M1"], ["M5", "M5"], ["M15", "M15"], ["H1", "H1"]].map(([v, label]) => {
            const on = timeframe === v;
            return (
              <button key={label} data-testid={`timeframe-${v || "auto"}`} onClick={() => setTimeframe(v)}
                className={`px-2 py-3 rounded-2xl border font-heading text-sm font-semibold transition-colors ${on ? "text-white" : "border-[#14233d] text-[#7f9bc4] hover:border-[#24406b]"}`}
                style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" } : undefined}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border rounded-3xl p-5 bg-[#0A1120] transition-colors"
        style={newsFilter ? { borderColor: "var(--sfx-accent)" } : { borderColor: "#14233d" }}
        data-testid="news-filter-card">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
            <Radar className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold">News Filter (CPI / NFP / FOMC)</span>
              <span className="font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">Optional</span>
            </div>
            <p className="text-xs text-[#5f7ba6] mt-0.5">
              When ON, Smart Scalper pauses opening new trades around high-impact US news (5 min before → 30 min after) to avoid dangerous spikes and spread expansion. Trading resumes automatically after the event.
            </p>
          </div>
          <button type="button" role="switch" aria-checked={newsFilter}
            onClick={() => setNewsFilter((v) => !v)} data-testid="news-filter-toggle"
            className="relative h-6 w-11 rounded-full transition-colors flex-shrink-0 mt-0.5"
            style={{ backgroundColor: newsFilter ? "var(--sfx-accent)" : "#1c2b45" }}>
            <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
              style={{ left: newsFilter ? "22px" : "2px" }} />
          </button>
        </div>
      </div>

      <div className="border rounded-3xl p-5 bg-[#0A1120] transition-colors"
        style={autoClose ? { borderColor: "var(--sfx-accent)" } : { borderColor: "#14233d" }}
        data-testid="auto-close-card">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
            <Target className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold">Auto Close on Profit</span>
              <span className="font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">All pairs</span>
            </div>
            <p className="text-xs text-[#5f7ba6] mt-0.5">
              Lè li aktive, Smart Scalper fèmen chak trade otomatikman sou TOUT pairs yo depi li rive nan pwofi. Ou ka aktive/dezaktive l nenpòt lè, menm pandan l ap trade an dirèk.
            </p>
          </div>
          <button type="button" role="switch" aria-checked={autoClose}
            onClick={() => setAutoClose((v) => !v)} data-testid="auto-close-toggle"
            className="relative h-6 w-11 rounded-full transition-colors flex-shrink-0 mt-0.5"
            style={{ backgroundColor: autoClose ? "var(--sfx-accent)" : "#1c2b45" }}>
            <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
              style={{ left: autoClose ? "22px" : "2px" }} />
          </button>
        </div>
        {autoClose && (
          <div className="mt-4 pt-4 border-t border-[#14233d] fade-up">
            <Label className="font-heading text-sm font-semibold">Take-Profit chak trade ($)</Label>
            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5f7ba6] font-mono-term text-sm">$</span>
              <Input type="number" step="0.5" min="0" value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)} placeholder="Kite vid = AI deside"
                data-testid="take-profit-input" className="bg-[#060c18] border-[#14233d] rounded-xl h-11 pl-7" />
            </div>
            <p className="text-[11px] text-[#5f7ba6] mt-2">Trade a fèmen depi pwofi flotan li rive montan sa a (an USD). Kite vid pou AI deside yon ti target entèlijan dapre gwosè lo a.</p>
          </div>
        )}
      </div>

      <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5 space-y-4" data-testid="backtest-card">
        <div className="flex items-center gap-2">
          <LineChart className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <Label className="font-heading font-semibold">Backtest — Jou Gwo Nouvèl (CPI/NFP)</Label>
        </div>
        <p className="text-[11px] text-[#5f7ba6] leading-relaxed">
          Wè yon <b className="text-[#8fb4e6]">estimasyon</b> sou kijan bòt la (ak tout pwoteksyon li yo) ta reyaji sou done mache resan yo, sitou nan moman volatilite wo (tankou nouvèl). Sa se yon estimasyon — li pa garanti rezilta.
        </p>
        <button onClick={runBacktest} disabled={btLoading} data-testid="run-backtest-button"
          className="w-full h-11 rounded-xl font-heading font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>
          {btLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Ap kalkile…</> : <><Activity className="h-4 w-4" /> Kouri Backtest</>}
        </button>

        {btResult && (
          <div className="space-y-3" data-testid="backtest-result">
            <div className="grid grid-cols-3 gap-2">
              {[
                { l: "Trade", v: btResult.totals.trades },
                { l: "Win rate", v: `${btResult.totals.win_rate}%` },
                { l: "Pwoteksyon", v: btResult.totals.protections_triggered },
              ].map((x) => (
                <div key={x.l} className="rounded-2xl border border-[#14233d] bg-[#060c18] p-3 text-center">
                  <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6]">{x.l}</div>
                  <div className="font-heading text-lg font-bold text-white mt-1">{x.v}</div>
                </div>
              ))}
            </div>
            <div className="divide-y divide-[#0f1a2e] border border-[#14233d] rounded-2xl overflow-hidden">
              {btResult.symbols.map((s) => (
                <div key={s.symbol} className="flex items-center gap-3 px-3 py-2.5" data-testid={`backtest-sym-${s.symbol}`}>
                  <span className="font-mono-term text-xs text-white w-20 shrink-0">{s.symbol}</span>
                  {s.error ? (
                    <span className="text-[11px] text-[#ff8f8f]">{s.error}</span>
                  ) : (
                    <div className="flex-1 flex items-center gap-3 text-[11px] font-mono-term text-[#8fb4e6]">
                      <span>{s.trades} trade</span>
                      <span className="text-[#22e07b]">{s.wins}W</span>
                      <span className="text-[#ff8f8f]">{s.losses}L</span>
                      <span className="ml-auto text-[#f5a623]">{(s.vol_blocks || 0) + (s.consec_blocks || 0)} poz</span>
                      <span className="text-[#5f7ba6]">{s.news_bars} bar nouvèl</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#5f7ba6]">Baze sou ~{btResult.bars_per_symbol} baraj {btResult.timeframe} · mòd {btResult.mode}. Estimasyon sèlman.</p>
          </div>
        )}
      </div>

      <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5 space-y-5" data-testid="risk-protection-card">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <Label className="font-heading font-semibold">Prop Firm Safety &amp; Loss Protection</Label>
          <span className="ml-auto font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#14233d] text-[#8fb4e6]">Pro</span>
        </div>

        {account?.type === "propfirm" && (
          <div data-testid="safety-stop-block">
            <div className="flex items-center justify-between mb-1">
              <span className="font-heading text-sm font-semibold">Safety Buffer</span>
              <span className="font-mono-term text-sm" style={{ color: "var(--sfx-accent)" }} data-testid="safety-stop-value">{safetyStop}%</span>
            </div>
            <p className="text-[11px] text-[#5f7ba6] mb-3">
              AutoTrade kanpe lè kont lan rive nan <b className="text-[#8fb4e6]">{safetyStop}%</b> nan chak limit prop firm (Daily Loss / Drawdown / Trailing) — anvan ou reyèlman kraze règ la. Rekòmande: 80%.
            </p>
            <Slider data-testid="safety-stop-slider" value={[safetyStop]} min={50} max={95} step={5}
              onValueChange={(v) => setSafetyStop(v[0])} className="my-2" />
            <div className="flex justify-between text-[10px] text-[#5f7ba6] font-mono-term">
              <span>50% (pi konsèvatif)</span><span>95%</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Repeat className="h-3.5 w-3.5 text-[#8fb4e6]" />
              <span className="font-heading text-xs font-semibold">Pèt konsekitif / pè</span>
            </div>
            <Input type="number" min="1" max="10" step="1" value={consecLoss}
              onChange={(e) => setConsecLoss(e.target.value)} data-testid="consec-loss-input"
              className="bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            <p className="text-[10px] text-[#5f7ba6] mt-1">Poz pè a jiska demen apre N pèt youn dèyè lòt.</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Power className="h-3.5 w-3.5 text-[#8fb4e6]" />
              <span className="font-heading text-xs font-semibold">Poz bòt (min)</span>
            </div>
            <Input type="number" min="5" max="1440" step="5" value={crossPause}
              onChange={(e) => setCrossPause(e.target.value)} data-testid="cross-pause-input"
              className="bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            <p className="text-[10px] text-[#5f7ba6] mt-1">2 pè an pèt nan 60 min → poz tout bòt la.</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Zap className="h-3.5 w-3.5 text-[#8fb4e6]" />
              <span className="font-heading text-xs font-semibold">Disjonktè ATR (%)</span>
            </div>
            <Input type="number" min="0" max="10" step="0.5" value={volAtr}
              onChange={(e) => setVolAtr(e.target.value)} data-testid="vol-atr-input"
              className="bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            <p className="text-[10px] text-[#5f7ba6] mt-1">Poz sou volatilite ekstrèm (0 = etenn).</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Activity className="h-3.5 w-3.5 text-[#8fb4e6]" />
              <span className="font-heading text-xs font-semibold">Poz volatilite (min)</span>
            </div>
            <Input type="number" min="1" max="240" step="1" value={volPause}
              onChange={(e) => setVolPause(e.target.value)} data-testid="vol-pause-input"
              className="bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            <p className="text-[10px] text-[#5f7ba6] mt-1">Konbyen tan pou tann mache a kalme.</p>
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={busy} data-testid="save-pairs-button"
        className="w-full h-12 rounded-full text-white font-semibold hover:opacity-90 transition-opacity"
        style={{ backgroundColor: "var(--sfx-accent)" }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Configuration"}
      </Button>

      <button onClick={closeAll} disabled={closingAll} data-testid="close-all-button"
        className="w-full h-12 rounded-full font-semibold border transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        style={{ borderColor: "#ff5b5b", color: "#ff8f8f", backgroundColor: "rgba(255,91,91,0.06)" }}>
        {closingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Power className="h-4 w-4" /> Fèmen Tout Trade Kounye a</>}
      </button>
    </div>
  );
}
